import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DAYS, type League } from '@lcc/shared';
import { api } from '../lib/api';
import { Card, Empty, ErrorNote, Loading, Status } from '../components/ui';
import { PageHeader } from '../App';

export function drawTimesLabel(l: League): string {
  if (l.drawTimes.length === 0) return 'No draw times set';
  const byDay = new Map<number, string[]>();
  for (const dt of l.drawTimes) {
    if (!byDay.has(dt.dayOfWeek)) byDay.set(dt.dayOfWeek, []);
    byDay.get(dt.dayOfWeek)!.push(dt.startTime);
  }
  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([dow, times]) => `${DAYS[dow]!.slice(0, 3)} ${times.join(', ')}`)
    .join(' · ');
}

export function LeaguesPage() {
  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  return (
    <>
      <PageHeader title="Leagues & teams"
        sub={leagues.data ? `${leagues.data.length} leagues this season` : undefined} />
      <div className="content">
        {leagues.isLoading ? <Loading what="Loading leagues" />
          : leagues.error ? <ErrorNote error={leagues.error} />
          : !leagues.data?.length ? <Empty title="No leagues yet" />
          : (
            <Card flush>
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>League</th><th>When</th><th>Format</th>
                      <th className="num">Teams</th><th className="num">Registered</th>
                      <th>Conveners</th><th>Registration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagues.data.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <Link to={`/leagues/${l.id}`}>{l.name}</Link>
                          {l.isCompetitive && <> <span className="pill pill--warn">competitive</span></>}
                        </td>
                        <td className="small muted nowrap">{drawTimesLabel(l)}</td>
                        <td className="small muted">{l.format.replace(/_/g, ' ')}</td>
                        <td className="num">{l.teamCount ?? 0}</td>
                        <td className="num">{l.registrationCount ?? 0}</td>
                        <td className="small muted">
                          {l.conveners.map((c) => c.fullName).join(', ') || '—'}
                        </td>
                        <td><Status value={l.registrationOpen ? 'open' : 'cancelled'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
      </div>
    </>
  );
}
